<template>
  <div class="editor-header-buttons-group">
    <div class="small-buttons-group" v-if="!isTreatmentShareV2">
      <!-- Settings button -->
      <a
        class="tab-trigger-btn text-link"
        :class="{ 'tab-trigger-btn--active': isShowSetting }"
        @click="$emit('showSetting')"
        v-tooltip="$i18nPresenter.t('Settings')"
      >
        <aha-icon
          name="setting"
          viewBox="0 0 24 24"
          width="18"
          height="18"
        />
      </a>

      <!-- Results button -->
      <a
        @click="$emit('showExport')"
        class="tab-trigger-btn text-link"
        :class="{ 'tab-trigger-btn--active': isShowResult }"
        v-tooltip="$i18nPresenter.t('Results')"
      >
        <aha-icon
          name="download"
          viewBox="0 0 24 24"
          width="18"
          height="18"
        />
      </a>
    </div>

    <div class="small-buttons-group" v-if="isTreatmentShareV2">
      <!-- Results button -->
      <a
        @click="$emit('showExport')"
        class="tab-trigger-btn text-link"
        :class="{ 'tab-trigger-btn--active': isShowResult }"
        v-tooltip="$i18nPresenter.t('Results')"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M11 6.25V2L4.75 2C4.33437 2 4 2.33437 4 2.75L4 17.25C4 17.6656 4.33437 18 4.75 18H15.25C15.6656 18 16 17.6656 16 17.25V7L11.75 7C11.3375 7 11 6.6625 11 6.25ZM13.3891 12.855L10.3759 15.8456C10.1681 16.0522 9.8325 16.0522 9.62469 15.8456L6.61156 12.855C6.29438 12.5403 6.51688 12 6.96312 12H9V9.5C9 9.22375 9.22375 9 9.5 9H10.5C10.7762 9 11 9.22375 11 9.5V12H13.0369C13.4831 12 13.7056 12.5403 13.3891 12.855ZM15.7812 5.28125L12.7219 2.21875C12.5813 2.07812 12.3906 2 12.1906 2H12V6L16 6V5.80938C16 5.6125 15.9219 5.42188 15.7812 5.28125Z" fill="#191F2D" />
        </svg>
      </a>

      <!-- Settings button -->
      <a
        class="tab-trigger-btn text-link"
        :class="{ 'tab-trigger-btn--active': isShowSetting }"
        @click="$emit('showSetting')"
        v-tooltip="$i18nPresenter.t('Settings')"
      >
        <aha-icon
          name="setting-top-bar"
        />
      </a>

      <a
        v-if="isShowHelpCenter"
        class="tab-trigger-btn text-link"
        href="https://help.ahaslides.com/"
        target="_blank"
        v-tooltip="tooltipHelp"
        @click="trackGAHelpCenter"
      >
        <aha-icon
          class="mr-0 xl:mr-1"
          name="question-top-bar"
        />
      </a>
    </div>

    <div class="preview-present-buttons-group">
      <PreviewButton class="hidden -mt-1 sm:block lg:ml-2" />
      <span class="hidden -mt-1 sm:inline">
        <aha-button
          class="aha-button-present"
          :disabled="isNewPresentation || isDisableEditPresentation"
          @mouseup.stop
        >
          <span
            class="flex items-center
                justify-center pl-2.5 pr-1.5 py-1.5"
            @click="onClickPresentButton"
          >
            <span class="w-5 h-5">
              <aha-icon
                name="play"
                viewBox="0 0 24 24"
                width="15"
                height="15"
                v-if="!waitPresenting"
              />
              <aha-spinner variant="white" v-else />
            </span>
            <span class="ml-1">{{ $i18nPresenter.t('Present') }}</span>
          </span>

          <span
            class="h-6 border-r no-margin border-primary-100"
            @click="onClickPresentButton"
          />

          <!-- dropdown trigger -->
          <span
            class="hidden sm:block no-margin pl-1 pr-2.5 py-1.5"
            @click="$emit('togglePresentOptions')"
            @mouseup.stop
          >
            <span>
              <aha-icon
                name="arrow"
                width="14"
                height="14"
                viewBox="0 0 24 24"
              />
            </span>
          </span>
        </aha-button>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories.js'
import mixpanelUtils from '@/utils/mixpanel.utils'
import PreviewButton from '@/components-v2/presentation-editor/middle/PreviewButton.vue'

export default {
  components: {
    PreviewButton,
  },
  props: {
    waitPresenting: {
      type: Boolean,
      default: false,
    },
  },
  data: () => {
    return {
      tooltipHelp: {
        content: 'Help',
      },
    }
  },
  mounted() {
    this.tooltipHelp.content = this.$i18nPresenter.t('Help')
  },
  computed: {
    ...mapGetters(['isTreatmentShareV2']),
    ...mapState({
      isNewPresentation: (state) => state.isNewPresentation,
      presentation: (state) => state.presentation,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      isReferPublicTemplate: (state) => state.isReferPublicTemplate,
    }),
    isShowSetting() {
      return this.presentation.id && this.presentation.showSetting
    },
    isShowResult() {
      return this.presentation.id && this.presentation.showExport
    },
    isShowHelpCenter() {
      return !this.isBackstage
    },
  },
  methods: {
    trackGAHelpCenter() {
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Header > Help')
    },
    onClickPresentButton() {
      if (this.waitPresenting) {
        return
      }
      const customProps = {
        ...mixpanelUtils.getPresentationContext(this.presentation),
        ...Utils.getSlideCountObject(this.presentation),
      }
      this.$emit('startPresentingMode')
      Utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'start presenting',
        'Presentation Editor > Present Button',
        this.presentation.id,
        customProps,
      )
      if (this.isReferPublicTemplate) {
        Utils.mixpanelTracking('Public template > Demo mode', 'Start presenting')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";

.editor-header-buttons-group {
  @apply flex items-center justify-end md:justify-between;
  .small-buttons-group {
    @apply hidden md:flex items-center justify-between;
  }

  &.backstage-buttons {
    @apply justify-end;
  }
}
.tab-trigger-btn {
  @apply relative top-1.5 items-center px-1.5 xl:px-2 pt-2 pb-6;
  // only apply transition to color. Prevent wierd background flicker when applying --active class
  transition-property: color;

  &--active {
    @apply z-20 bg-base-0 rounded-t-md text-primary-100;
  }
}
.preview-present-buttons-group {
  @apply flex gap-x-3;
}
.aha-button-present {
  @apply relative flex items-center justify-center p-0;
  span.no-margin {
    margin-left: 0;
  }
}
</style>
